/**
 * Internal clipboard store — holds copied/cut task IDs (§21 Fas 27c).
 *
 * This is separate from the system clipboard. When Cmd+C/X is pressed
 * with tasks selected, the task IDs are stored here AND a plain-text
 * version of the titles is written to the system clipboard.
 *
 * On Cmd+V, the internal clipboard is checked first:
 * - If fresh (< 30 min) → duplicate (copy) or move (cut) the stored tasks
 * - If stale or empty → fall back to system clipboard parse (Fas 17c)
 *
 * Zustand store with non-React access via getState() for handlers.
 */

import { create } from "zustand"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ClipboardOperation = "copy" | "cut"

export interface ClipboardState {
  /** IDs of the tasks that were copied or cut */
  taskIds: string[]
  /** Whether the tasks were copied or cut */
  operation: ClipboardOperation | null
  /** View ID where the operation was initiated */
  sourceViewId: string | null
  /** Epoch ms when the clipboard was set */
  timestamp: number
}

interface ClipboardActions {
  /** Set clipboard contents (copy or cut) */
  set: (
    taskIds: string[],
    operation: ClipboardOperation,
    sourceViewId: string,
  ) => void
  /** Clear clipboard (after paste-move or manual clear) */
  clear: () => void
  /** Check if clipboard is stale (> 30 min) */
  isStale: () => boolean
  /** Check if clipboard has usable content (non-empty + not stale) */
  hasContent: () => boolean
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Internal clipboard expires after 30 minutes */
const STALE_THRESHOLD_MS = 30 * 60 * 1000

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useClipboardStore = create<ClipboardState & ClipboardActions>(
  (set, get) => ({
    // State
    taskIds: [],
    operation: null,
    sourceViewId: null,
    timestamp: 0,

    // Actions
    set: (taskIds, operation, sourceViewId) =>
      set({
        taskIds,
        operation,
        sourceViewId,
        timestamp: Date.now(),
      }),

    clear: () =>
      set({
        taskIds: [],
        operation: null,
        sourceViewId: null,
        timestamp: 0,
      }),

    isStale: () => {
      const { timestamp, taskIds } = get()
      if (taskIds.length === 0) return true
      return Date.now() - timestamp > STALE_THRESHOLD_MS
    },

    hasContent: () => {
      const { taskIds } = get()
      if (taskIds.length === 0) return false
      return !get().isStale()
    },
  }),
)
